import { Link } from "react-router-dom";
import { Compass, Heart, ShieldCheck, ArrowUpRight, Target, Eye, Sparkles, CheckCircle2 } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, t: "Integrity over speed",   d: "We tell clients what they need to hear not just what they want." },
  { icon: Heart,       t: "Provider first",         d: "Every recommendation considers how it lands for staff and participants." },
  { icon: Compass,     t: "Practical empowerment",  d: "Compliance that works in the real world, not just on paper." },
];

const STANDARDS = [
  "Personalised, transparent advice not template responses",
  "Clear communication in plain English",
  "Practical recommendations that fit how you really operate",
  "Documentation that reflects real world operations",
  "Long term partnership beyond the contract",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="container-pad pt-16 pb-20">
        <div className="overline">About Elevate</div>
        <h1 className="font-display text-5xl sm:text-6xl text-navy mt-6 tracking-tight max-w-4xl leading-[1.05]">
          Australia's <span className="text-brand-gradient font-serif-mark italic">Provider Success</span> Consultancy.
        </h1>
        <p className="text-muted2 mt-8 max-w-2xl leading-relaxed text-lg">
          Elevate Consulting was founded to do more than register providers. We exist to help disability service
          organisations build sustainable, compliant and exceptional businesses across the entire provider lifecycle.
        </p>
      </section>

      {/* MISSION + VISION */}
      <section className="container-pad pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-soft p-10 md:p-12">
            <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
              <Target size={22} className="text-teal" />
            </div>
            <div className="overline mt-6">Our mission</div>
            <p className="font-display text-2xl text-navy mt-4 leading-snug">
              To simplify compliance while empowering providers with the systems, knowledge and strategic support required
              to establish, operate and grow successful disability service organisations.
            </p>
          </div>
          <div className="p-10 md:p-12 rounded-3xl bg-navy text-canvas relative overflow-hidden grain shadow-xl shadow-navy/20">
            <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center relative">
              <Eye size={22} className="text-teal-400" />
            </div>
            <div className="overline mt-6 relative" style={{ color: '#A7E0E6' }}>Our vision</div>
            <p className="font-display text-2xl mt-4 leading-snug relative">
              To become Australia's most respected Provider Success Consultancy setting the benchmark for innovation,
              professionalism and client experience in the disability services advisory sector.
            </p>
          </div>
        </div>
      </section>

      {/* SPLIT STORY */}
      <section className="container-pad pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <img
              src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
              alt="Elevate consultants in session"
              className="w-full aspect-[5/4] object-cover rounded-3xl"
            />
          </div>
          <div className="md:col-span-5 flex flex-col justify-center">
            <div className="overline">Our story</div>
            <h2 className="font-display text-3xl sm:text-4xl text-navy mt-4 tracking-tight leading-[1.1]">
              Built by people who've audited, registered and operated NDIS services.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              Elevate was established in response to the evolving needs of Australia's disability services sector. Our
              team brings combined experience across quality auditing, clinical governance, plan management and
              operations independent, and entirely in your corner.
            </p>
            <p className="text-muted2 mt-4 leading-relaxed">
              We believe compliance should be a competitive advantage not an obstacle. That belief sits at the heart
              of every engagement.
            </p>
          </div>
        </div>
      </section>

      {/* THE ELEVATE STANDARD */}
      <section className="bg-sand py-24">
        <div className="container-pad grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">The Elevate Standard™</div>
            <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 tracking-tight leading-[1.08]">
              Our promise to every client.
            </h2>
            <p className="text-muted2 mt-6 leading-relaxed">
              The Elevate Standard™ is our internal quality commitment a published bar for how we work with every
              provider we partner with.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-4">
              {STANDARDS.map((s) => (
                <li key={s} className="flex items-start gap-4 card-soft p-6">
                  <CheckCircle2 size={22} className="text-teal flex-shrink-0 mt-0.5" />
                  <span className="text-ink leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="container-pad py-24">
        <div className="overline">Our values</div>
        <h2 className="font-display text-4xl sm:text-5xl text-navy mt-4 max-w-2xl tracking-tight leading-[1.08]">
          How we work published, not hidden in a slide deck.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {VALUES.map((v) => (
            <div key={v.t} className="card-soft p-8">
              <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center">
                <v.icon size={22} className="text-teal" />
              </div>
              <div className="font-display text-xl text-navy mt-5">{v.t}</div>
              <p className="text-muted2 mt-3 text-sm leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-pad py-24">
        <div className="bg-brand-gradient text-canvas rounded-3xl p-12 md:p-16 grain relative overflow-hidden">
          <div className="overline" style={{ color: '#A7E0E6' }}>Let's talk</div>
          <h3 className="font-display text-3xl sm:text-4xl mt-4 max-w-2xl tracking-tight leading-[1.1]">
            See if we're a good fit book a free 30 minute discovery call.
          </h3>
          <Link to="/contact" data-testid="about-cta" className="btn-light-on-navy mt-8">
            Book my call <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
